import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { ErrorText, cx } from "../../ui";
import { Composer } from "./Composer";
import { MessageImages } from "./MessageImages";
import { handleFor } from "./mentions";
import { RichText } from "./markdown";
import { displayName, type Member, type Message, type MessageGroup, type Pending } from "./model";
import { RefChip } from "./RefChip";

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

export function formatTime(at: Date, now: Date = new Date()): string {
  const time = at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (sameDay(at, now)) return time;
  return `${at.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${time}`;
}

export interface ListContext {
  workspaceId: string;
  channelId: string;
  myUid: string;
  members: readonly Member[];
  byUid: ReadonlyMap<string, Member>;
}

function useIsMe(ctx: ListContext) {
  const me = ctx.byUid.get(ctx.myUid);
  const mine = me === undefined ? null : handleFor(me, ctx.members);
  return (handle: string) => mine !== null && handle.toLowerCase() === mine;
}

export function GroupView({ group, ctx }: { group: MessageGroup; ctx: ListContext }) {
  const isMe = useIsMe(ctx);
  const author = displayName(ctx.byUid.get(group.authorUid), "Someone");
  const [first] = group.messages;
  if (group.kind === "system" && first !== undefined) {
    return (
      <li className="my-2 flex flex-col items-center gap-1 rounded-md bg-surface-2/60 px-3 py-2 text-center" data-kind="system">
        <p className="text-sm italic text-muted">
          <span className="font-medium not-italic text-text">{author}</span> {first.body}
          <span className="ml-2 text-xs not-italic">{formatTime(first.createdAt)}</span>
        </p>
        {first.refs.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-1">
            {first.refs.map((r) => (
              <RefChip key={`${r.kind}:${r.id}`} workspaceId={ctx.workspaceId} item={r} />
            ))}
          </div>
        ) : null}
      </li>
    );
  }
  return (
    <li className="mt-3 flex flex-col gap-0.5" data-kind="user">
      <p className="text-sm">
        <span className="font-semibold text-text">{author}</span>{" "}
        <time dateTime={group.startedAt.toISOString()} className="text-xs text-muted">
          {formatTime(group.startedAt)}
        </time>
      </p>
      <ul className="flex flex-col gap-0.5">
        {group.messages.map((m) => (
          <MessageRow key={m.id} message={m} ctx={ctx} isMe={isMe} />
        ))}
      </ul>
    </li>
  );
}

function MessageRow({ message, ctx, isMe }: { message: Message; ctx: ListContext; isMe: (h: string) => boolean }) {
  const trpc = useTRPC();
  const [editing, setEditing] = useState(false);
  const edit = useMutation(trpc.chat.edit.mutationOptions({ onSuccess: () => setEditing(false) }));
  const mine = message.authorUid === ctx.myUid && message.kind === "user";

  if (editing) {
    return (
      <li className="flex flex-col gap-1 py-1">
        <Composer
          label="Edit message"
          members={ctx.members}
          initial={message.body}
          submitLabel={edit.isPending ? "Saving…" : "Save"}
          onCancel={() => setEditing(false)}
          onSend={(body) =>
            edit.mutate({ workspaceId: ctx.workspaceId, channelId: ctx.channelId, messageId: message.id, body })
          }
        />
        {edit.isError ? <ErrorText>{errorMessage(edit.error)}</ErrorText> : null}
      </li>
    );
  }

  return (
    <li className="group relative -mx-2 rounded-md px-2 py-0.5 hover:bg-surface-2/50" data-message-id={message.id}>
      {message.body.length === 0 ? null : <RichText body={message.body} isMe={isMe} />}
      {message.editedAt === null ? null : <span className="text-xs text-muted">(edited)</span>}
      <MessageImages workspaceId={ctx.workspaceId} channelId={ctx.channelId} images={message.images} />
      {message.refs.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-1">
          {message.refs.map((r) => (
            <RefChip key={`${r.kind}:${r.id}`} workspaceId={ctx.workspaceId} item={r} />
          ))}
        </div>
      ) : null}
      {mine ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="absolute right-1 top-0.5 rounded px-1.5 text-xs text-muted opacity-60 after:absolute after:-inset-x-0.5 after:-inset-y-2.5 hover:bg-surface hover:text-text group-hover:opacity-100 focus-visible:opacity-100"
        >
          Edit<span className="sr-only"> message</span>
        </button>
      ) : null}
    </li>
  );
}

export function PendingRow({ item, onRetry }: { item: Pending; onRetry: () => void }) {
  return (
    <li className={cx("mt-1 flex flex-col gap-0.5 opacity-70", item.status === "failed" && "opacity-100")} data-pending="true">
      {item.body.length === 0 ? null : <p className="whitespace-pre-wrap break-words text-sm text-text">{item.body}</p>}
      {item.files.length === 0 ? null : (
        <p className="text-xs text-muted">{item.files.length === 1 ? "1 image" : `${item.files.length} images`}</p>
      )}
      {item.status === "sending" ? (
        <p className="text-xs text-muted">Sending…</p>
      ) : (
        <p className="text-xs text-danger">
          Not sent.{" "}
          <button type="button" className="underline" onClick={onRetry}>
            Try again
          </button>
        </p>
      )}
    </li>
  );
}
